// src/utils/teamAttributes.ts
//
// Pure helpers for the Settings → Team screen: grouping attribute rows,
// formatting chip labels, and deriving <datalist> suggestion vocabularies.
// Kept free of React/Supabase so they're unit-testable (Story 1.4).

import type { YakUserAttribute, YakUserAttributeType } from '../types/yak'

// The fixed vocabulary; region is rendered/added differently (FK, not text).
export const ATTRIBUTE_TYPES: YakUserAttributeType[] = [
  'brand',
  'region',
  'channel',
  'payer',
]

export function attributesForUser(
  attributes: YakUserAttribute[],
  userId: string
): YakUserAttribute[] {
  return attributes.filter(a => a.user_id === userId)
}

// Group a user's attribute rows by type, preserving ATTRIBUTE_TYPES order.
export function groupByType(
  attributes: YakUserAttribute[]
): Array<{ type: YakUserAttributeType; items: YakUserAttribute[] }> {
  return ATTRIBUTE_TYPES.map(type => ({
    type,
    items: attributes.filter(a => a.attribute_type === type),
  })).filter(group => group.items.length > 0)
}

// A labeled chip, e.g. "brand: neffy" or "region: East". Region rows carry an
// FK, so the caller passes the resolved node name; a missing name (node fetch
// lag) degrades to a readable placeholder rather than a blank chip.
export function chipLabel(
  attribute: YakUserAttribute,
  regionName?: string | null
): string {
  const display =
    attribute.attribute_type === 'region'
      ? regionName || 'unknown region'
      : attribute.value || ''
  return `${attribute.attribute_type}: ${display}`
}

// Distinct, trimmed, case-preserved position labels already in use — the
// <datalist> vocabulary. Sorted for a stable dropdown.
export function positionsInUse(
  members: Array<{ position: string | null }>
): string[] {
  const set = new Set<string>()
  for (const m of members) {
    const p = m.position?.trim()
    if (p) set.add(p)
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b))
}

// Distinct, trimmed values already assigned for a given text attribute type
// (brand/channel/payer) — the per-type <datalist> vocabulary.
export function valuesInUse(
  attributes: YakUserAttribute[],
  type: YakUserAttributeType
): string[] {
  const set = new Set<string>()
  for (const a of attributes) {
    if (a.attribute_type !== type) continue
    const v = a.value?.trim()
    if (v) set.add(v)
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b))
}

// Trim; empty/whitespace collapses to NULL (an unset position, not a blank).
export function normalizePosition(input: string): string | null {
  const trimmed = input.trim()
  return trimmed || null
}
